import React from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  List,
  ListItem,
  Chip,
  Stack,
  Divider,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Tooltip,
  Grid
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { Pill, FileText, Scissors } from 'lucide-react';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import Loading from '../Loading';

const ListDataAccordion = ({
  expanded,
  handleAccordionChange,
  listData,
  fetchFunctions,
  partsLoaded,
  initialQuery
}) => {
  const theme = useTheme();
  const { diagnosis, medications, procedures } = listData;

  const CompactListCard = ({
    title,
    icon: Icon,
    data,
    isLoading,
    onRetry,
    tooltipLabel
  }) => (
    <Paper
      sx={{
        border: `1px solid ${theme.palette.divider}`,
        backgroundColor: theme.palette.surface.raised,
        boxShadow: theme.palette.appShadow.card,
      }}
    >
      <Box padding={1}>
        <Typography
          sx={{
            fontSize: "0.87rem",
            color: theme.palette.text.secondary,
            fontWeight: 400,
          }}
        >
          {title}
        </Typography>
        {isLoading ? (
          <Loading />
        ) : data?.response === "Retry" ? (
          <Button color="error" onClick={onRetry}>Retry</Button>
        ) : data?.response?.length > 0 ? (
          <List sx={{ p: 0 }}>
            {data.response.slice(0, 10).map((item, index) => (
              <React.Fragment key={index}>
                <Tooltip
                  title={`${item.name} (${item.count?.toLocaleString() || '0'})`}
                  placement="top"
                  arrow
                >
                  <ListItem
                    sx={{
                      px: 0,
                      py: 0.75,
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      minHeight: 'auto',
                      cursor: 'pointer',
                      borderRadius: 0.5,
                      '&:hover': {
                        backgroundColor: 'action.hover',
                      }
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flex: 1, mr: 0.5, minWidth: 0 }}>
                      <Chip
                        label={index + 1}
                        size="small"
                        sx={{
                          minWidth: 16,
                          height: 14,
                          fontSize: '0.75rem',
                          fontWeight: 600,
                          backgroundColor: 'primary.main',
                          color: 'primary.contrastText',
                          flexShrink: 0,
                          '& .MuiChip-label': {
                            px: 0.4,
                          }
                        }}
                      />
                      <Typography
                        variant="body2"
                        sx={{
                          fontSize: '0.875rem',
                          fontWeight: 400,
                          color: 'text.primary',
                          lineHeight: 1.4,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          maxWidth: '150px'
                        }}
                      >
                        {item.name}
                      </Typography>
                    </Box>

                    <Chip
                      label={item.count?.toLocaleString() || '0'}
                      size="small"
                      variant="outlined"
                      sx={{
                        fontSize: '0.75rem',
                        fontWeight: 600,
                        height: 16,
                        borderColor: 'primary.light',
                        color: theme.palette.mode === 'dark' ? 'primary.light' : 'primary.dark',
                        flexShrink: 0,
                        '& .MuiChip-label': {
                          px: 0.5,
                        }
                      }}
                    />
                  </ListItem>
                </Tooltip>
                {index < Math.min(data.response.length - 1, 9) && (
                  <Divider sx={{ my: 0.25 }} />
                )}
              </React.Fragment>
            ))}
          </List>
        ) : (
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ fontSize: '0.75rem', textAlign: 'center', py: 2 }}
          >
            No data available
          </Typography>
        )}
      </Box>
    </Paper>
  );

  return (
    <Accordion
      expanded={expanded === "listData"}
      onChange={handleAccordionChange("listData")}
      disabled={Object.keys(diagnosis).length === 0 && Object.keys(medications).length === 0 && Object.keys(procedures).length === 0}
    >
      <AccordionSummary
        expandIcon={<ExpandMoreIcon />}
        aria-controls="panel1a-content"
        className="accordion-summary"
      >
        <Typography
          variant="h6"
          width={"100%"}
          className="accordian-title"
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              fontSize: "1rem",
            }}
          >
            <Grid size={4}
              sx={{
                paddingLeft: "12px",
              }}
            >
              Top 10 Diagnosis
            </Grid>
            <Grid size={4}
              sx={{
                borderLeft: `1px solid ${theme.palette.divider}`,
                borderRight: `1px solid ${theme.palette.divider}`,
                paddingLeft: "12px",
              }}
            >
              Top 10 Medications
            </Grid>
            <Grid size={4}
              sx={{
                paddingLeft: "12px",
              }}
            >
              Top 10 Procedures
            </Grid>
          </Box>
        </Typography>
      </AccordionSummary>

      <AccordionDetails sx={{ p: theme.spacing(1.5) }}>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={theme.spacing(2)}
          sx={{
            '& > *': {
              flex: { md: 1 },
              minWidth: 0,
              maxWidth: { md: `calc(33.333% - ${theme.spacing(1.5)}px)` }
            }
          }}
        >
          <CompactListCard
            title="Top 10 Diagnosis"
            icon={FileText}
            data={diagnosis}
            isLoading={!partsLoaded.topTenDiagnosis}
            onRetry={() => fetchFunctions.fetchTopTenDiagnosisData(initialQuery)}
            tooltipLabel={diagnosis?.toolTipLabel}
          />

          <CompactListCard
            title="Top 10 Medications"
            icon={Pill}
            data={medications}
            isLoading={!partsLoaded.topTenMedications}
            onRetry={() => fetchFunctions.fetchTopTenMedicationsData(initialQuery)}
            tooltipLabel={medications?.toolTipLabel}
          />

          <CompactListCard
            title="Top 10 Procedures"
            icon={Scissors}
            data={procedures}
            isLoading={!partsLoaded.topTenProcedures}
            onRetry={() => fetchFunctions.fetchTopTenProceduresData(initialQuery)}
            tooltipLabel={procedures?.toolTipLabel}
          />
        </Stack>
      </AccordionDetails>
    </Accordion>
  );
};

export default ListDataAccordion;